import { gql } from "@apollo/client";
import {
  BUTTON_EXTERNAL_RECORD,
  BUTTON_RECORD,
  DOWNLOAD_BUTTON_RECORD,
  HUBSPOT_BUTTON_RECORD,
} from "./fragments/button";

export { BUTTON_EXTERNAL_RECORD, BUTTON_RECORD, DOWNLOAD_BUTTON_RECORD, HUBSPOT_BUTTON_RECORD };

// ============================================================================
// SHARED FRAGMENTS (used across multiple domains)
// ============================================================================

export const SITE_FIELDS = gql`
  fragment SiteFields on Site {
    faviconMetaTags {
      attributes
      content
      tag
    }
    globalSeo {
      fallbackSeo {
        description
        title
      }
    }
  }
`;

export const GENERAL_FIELDS = gql`
  ${BUTTON_RECORD}
  ${BUTTON_EXTERNAL_RECORD}
  ${DOWNLOAD_BUTTON_RECORD}
  ${HUBSPOT_BUTTON_RECORD}
  fragment GeneralFields on GeneralRecord {
    id
    address
    email
    phone
    vat
    logo {
      id
      responsiveImage(imgixParams: { auto: format, fit: max, h: "50" }) {
        alt
        height
        src
        title
        width
      }
    }
    footerIntro
    footerNav {
      title
      links {
        id
        linkText
        link {
          __typename
          ... on ExternalLinkRecord {
            url
          }
          ... on InternalLinkRecord {
            link {
              __typename
              ... on ServiceRecord {
                slug
                parent {
                  slug
                }
              }
              ... on SearchRecord {
                slug
              }
              ... on PageRecord {
                slug
              }
              ... on OfficeRecord {
                slug
                parent {
                  slug
                  parent {
                    slug
                    parent {
                      slug
                      parent {
                        slug
                      }
                    }
                  }
                }
              }
              ... on MunicipalityRecord {
                slug
              }
              ... on ExpertiseRecord {
                slug
              }
              ... on ContactRecord {
                slug
              }
              ... on ArticleRecord {
                slug
              }
              ... on TargetGroupRecord {
                slug
                parent {
                  slug
                }
              }
            }
          }
        }
      }
    }
    mainNavigation {
      ... on LinkRecord {
        id
        linkText
        link {
          __typename
          ... on ExternalLinkRecord {
            url
          }
          ... on InternalLinkRecord {
            link {
              __typename
              ... on ServiceRecord {
                slug
                parent {
                  slug
                }
              }
              ... on SearchRecord {
                slug
              }
              ... on PageRecord {
                slug
              }
              ... on OfficeRecord {
                slug
                parent {
                  slug
                  parent {
                    slug
                    parent {
                      slug
                      parent {
                        slug
                      }
                    }
                  }
                }
              }
              ... on MunicipalityRecord {
                slug
              }
              ... on ExpertiseRecord {
                slug
              }
              ... on ContactRecord {
                slug
              }
              ... on ArticleRecord {
                slug
              }
              ... on TargetGroupRecord {
                slug
                parent {
                  slug
                }
              }
            }
          }
        }
      }
      ... on NavFlyoutGroupRecord {
        id
        title
        columns {
          __typename
          ... on NavFlyoutLinkListRecord {
            links {
              id
              linkText
              link {
                __typename
                ... on ExternalLinkRecord {
                  url
                }
                ... on InternalLinkRecord {
                  link {
                    __typename
                    ... on ServiceRecord {
                      slug
                    }
                    ... on SearchRecord {
                      slug
                    }
                    ... on PageRecord {
                      slug
                    }
                    ... on OfficeRecord {
                      slug
                    }
                    ... on MunicipalityRecord {
                      slug
                    }
                    ... on ExpertiseRecord {
                      slug
                    }
                    ... on ContactRecord {
                      slug
                    }
                    ... on ArticleRecord {
                      slug
                    }
                    ... on TargetGroupRecord {
                      slug
                    }
                  }
                }
              }
            }
          }
          ... on NavFlyoutIntroRecord {
            __typename
            title
            text {
              value
            }
            cta {
              ...ButtonBlock
              ...DownloadButtonBlock
              ...HubspotButtonBlock
            }
          }
        }
      }
    }
    secondaryNavigation {
      ... on LinkRecord {
        id
        linkText
        link {
          __typename
          ... on ExternalLinkRecord {
            url
          }
          ... on InternalLinkRecord {
            link {
              __typename
              ... on ServiceRecord {
                slug
                parent {
                  slug
                }
              }
              ... on SearchRecord {
                slug
              }
              ... on PageRecord {
                slug
              }
              ... on OfficeRecord {
                slug
                parent {
                  slug
                  parent {
                    slug
                    parent {
                      slug
                      parent {
                        slug
                      }
                    }
                  }
                }
              }
              ... on MunicipalityRecord {
                slug
              }
              ... on ExpertiseRecord {
                slug
              }
              ... on ContactRecord {
                slug
              }
              ... on ArticleRecord {
                slug
              }
              ... on TargetGroupRecord {
                slug
                parent {
                  slug
                }
              }
            }
          }
        }
      }
    }
    navCta {
      ... on ButtonRecord {
        ...ButtonBlock
      }
      ... on ButtonExternalRecord {
        ...ButtonExternalBlock
      }
      ... on DownloadButtonRecord {
        ...DownloadButtonBlock
      }
      ... on HubspotButtonRecord {
        ...HubspotButtonBlock
      }
    }
    doormatImage {
      responsiveImage(
        imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "1500", h: "200", q: "75" }
      ) {
        alt
        height
        src
        title
        width
      }
    }
    subfooterNav {
      id
      linkText
      link {
        __typename
        ... on ExternalLinkRecord {
          url
        }
        ... on InternalLinkRecord {
          link {
            __typename
            ... on ServiceRecord {
              slug
            }
            ... on SearchRecord {
              slug
            }
            ... on PageRecord {
              slug
            }
            ... on OfficeRecord {
              slug
            }
            ... on MunicipalityRecord {
              slug
            }
            ... on ExpertiseRecord {
              slug
            }
            ... on ContactRecord {
              slug
            }
            ... on ArticleRecord {
              slug
            }
            ... on TargetGroupRecord {
              slug
            }
          }
        }
      }
    }
    socialLinks {
      id
      url
      icon
    }
    hubspotGeneralSegment
    newsletterSuccessPage {
      __typename
      id
      slug
    }
    newsletterErrorPage {
      __typename
      id
      slug
    }
    addressStreet
    addressCity
    addressPostcode
    addressCountry
    geoMidpoint {
      latitude
      longitude
    }
  }
`;

export const PAGE_SETTINGS = gql`
  fragment PageSettings on PageSettingRecord {
    id
    enableDoormat
  }
`;

export const MUNICIPALITY_FIELDS = gql`
  fragment MunicipalityFields on MunicipalityRecord {
    id
    slug
    title
    intro {
      value
    }
    city
    coordinates {
      latitude
      longitude
    }
  }
`;

export const ARTICLE_FIELDS = gql`
  fragment ArticleFields on ArticleRecord {
    __typename
    tag {
      id
      title
    }
    secondaryTags {
      id
      title
    }
    id
    slug
    title
    shortTitle
    intro {
      value
    }
    shortIntro {
      value
    }
    date
    _updatedAt
    author {
      __typename
      id
      name
      functionTitle
      slug
      slugs: _allSlugLocales {
        locale
        value
      }
      profilePicture {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "160", h: "160", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
    }
    reviewer {
      __typename
      id
      name
      functionTitle
      slug
      slugs: _allSlugLocales {
        locale
        value
      }
      profilePicture {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "160", h: "160", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
    }
  }
`;

export const FOOTER_OVERRIDES = gql`
  fragment FooterOverrides on FooterOverrideRecord {
    footerLogo {
      id
      responsiveImage(imgixParams: { auto: format, fit: max, h: "125", q: "75", dpr: "2" }) {
        alt
        height
        src
        title
        width
      }
    }
    footerSecondaryLogo {
      id
      responsiveImage(imgixParams: { auto: format, fit: max, h: "125", q: "75", dpr: "2" }) {
        alt
        height
        src
        title
        width
      }
    }
    footerIntro(markdown: true)
    kmoRegistrationNumbers(markdown: true)
    vat
    footerCookiePolicy {
      ... on PageRecord {
        slug
      }
    }
    footerPrivacyPolicy {
      ... on PageRecord {
        slug
      }
    }
    footerTermsConditions {
      ... on PageRecord {
        slug
      }
    }
    socialLinks {
      id
      url
      icon
    }
  }
`;

export const OFFICE_LOCATION = gql`
  fragment OfficeLocation on OfficeLocationRecord {
    id
    title
    slug
    officeAddress
    email
    phone
  }
`;
